import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ShoppingBag, Plus } from "lucide-react";
import { NAV_LINKS, BRAND } from "../data/staticData";
import { useCart } from "./CartContext";
import { useAddOns } from "./AddOnsContext";
const LOGO = BRAND.logos.primary;

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { count } = useCart();
  const { selected: addons, pricing: addonPricing } = useAddOns();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all ${
        scrolled
          ? "backdrop-blur-xl bg-black/70 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="container-fld flex items-center justify-between h-20 md:h-24">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src={LOGO}
            alt="Frontline Defense"
            className="h-16 md:h-20 w-auto transition-transform duration-300 group-hover:scale-105 drop-shadow-[0_0_18px_rgba(124,255,0,0.4)]"
          />
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-mono-tactical text-[10px] text-[#7CFF00] tracking-[0.25em] uppercase">
              // Defend Your Own
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-tactical uppercase text-sm tracking-widest px-4 py-2 transition-colors relative ${
                  isActive
                    ? "text-[#7CFF00]"
                    : "text-white/80 hover:text-[#7CFF00]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {link.label}
                  {isActive && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-[#7CFF00]" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {addons.length > 0 && (
            <Link
              to="/booking"
              data-testid="nav-addons-indicator"
              aria-label={`${addons.length} permit add-on${addons.length === 1 ? "" : "s"} selected, +$${addonPricing.total}`}
              className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 border border-[#7CFF00] bg-[#7CFF00]/10 text-[#7CFF00] font-tactical uppercase text-[10px] tracking-[0.2em] hover:bg-[#7CFF00] hover:text-black transition-colors shadow-[0_0_18px_rgba(124,255,0,0.35)]"
            >
              <Plus className="w-3 h-3" />
              <span className="flex items-center gap-1">
                {addons.map((c) => (
                  <span
                    key={c}
                    className="px-1 border border-[#7CFF00]/60 text-[10px]"
                  >
                    {c}
                  </span>
                ))}
              </span>
              <span
                data-testid="nav-addons-total"
                className="font-bold text-xs"
              >
                +${addonPricing.total}
              </span>
            </Link>
          )}
          <Link
            to="/cart"
            data-testid="nav-cart-button"
            className="relative p-2 text-white hover:text-[#7CFF00] transition-colors"
            aria-label="Cart"
          >
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span
                data-testid="nav-cart-count"
                className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#7CFF00] text-black text-[10px] font-bold flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </Link>
          <Link
            to="/booking"
            data-testid="nav-cta-book"
            className="hidden md:inline-flex btn-primary text-sm py-2 px-4"
          >
            Book Training
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 text-white"
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
        >
          <div className="container-fld py-4 flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-mobile-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-tactical uppercase tracking-widest px-3 py-3 border-l-2 transition-colors ${
                    isActive
                      ? "border-[#7CFF00] text-[#7CFF00]"
                      : "border-transparent text-white/80 hover:text-[#7CFF00]"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/booking"
              data-testid="nav-mobile-cta-book"
              className="btn-primary mt-3 justify-center"
            >
              Book Training
            </Link>
            <a
              href={`tel:${BRAND.phoneRaw}`}
              className="font-mono-tactical text-xs text-[#7CFF00] tracking-widest uppercase mt-2 text-center"
            >
              {BRAND.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
